import { ApiError, type ApiClient, type HttpMethod } from '@platform/api-client';
import type {
  AdminIntegration,
  AdminInstall,
  CrmMapping,
  CrmMappingResponse,
  CrmPatientStatusResponse,
  CrmResyncResponse,
  CrmSyncNowRequest,
  CrmSyncNowResponse,
  HealthProbe,
  IntegrationCall,
  IntegrationCallQuery,
  IntegrationHealthCheck,
  PageInfo,
  PutCredentialsRequest,
} from '@platform/contracts';

/**
 * The clinic admin's integrations seam (MASTER_PLAN M3.9, Docs/API.md §2.10, FR-ADMIN-09).
 *
 * Every method is one endpoint, named and shaped the way `Docs/API.md` spells it, and the
 * types are the ones the API validates against (`@platform/contracts/integrations`) rather
 * than a second set invented here. Two implementations satisfy it: this file, over
 * `@platform/api-client`, and `integrations.mock.ts`, which reproduces the same behaviours
 * in memory for tests and for `VITE_USE_MOCKS=true`. Which one a screen gets is decided in
 * `integrations-client-context.tsx` and nowhere else.
 *
 * ## What never crosses this seam
 *
 * A credential. `PUT .../credentials` sends one and nothing returns one: the API has no
 * field that could carry it back (SECURITY.md §7.4, `packages/contracts/src/integrations.ts`).
 * What comes back is `credHint`, a display-only map of last-4s. The form's in-memory state
 * is the only place a secret exists in the browser, and it is cleared on submit.
 *
 * ## Permissions are the server's
 *
 * `integrations.read` covers the two reads, the health history and the call log;
 * `integrations.connect` covers settings and enable; `integrations.test` the probe;
 * `integrations.disable` the disconnect and the turn-off; credentials additionally need
 * `integrations.rotate_credentials`. Three routes are **step-up** and two are
 * **no-impersonation**; both are enforced by the API. A control this UI disables is a
 * courtesy and never the enforcement point (rules §17.2).
 */

/* --------------------------------------------------------------------- types */

/** A cursor page exactly as `Docs/API.md` §1.7 returns one. */
export interface IntegrationPage<Row> {
  data: Row[];
  page: PageInfo;
}

/** `{ health, install }` — what the two probing routes answer. */
export interface ProbeResult {
  health: HealthProbe;
  install: AdminInstall;
}

/** The query the Activity tab sends. A subset of `IntegrationCallQuery`, camelCase on the wire. */
export type CallQuery = Partial<Pick<IntegrationCallQuery, 'status' | 'operation' | 'limit'>> & {
  cursor?: string | null;
};

export interface HealthQuery {
  limit?: number;
  cursor?: string | null;
}

/**
 * The CRM shapes, unwrapped from their envelopes. Aliases of `@platform/contracts`, never
 * a second declaration of the same thing: the mapping the form edits is the object the
 * API validates with `CrmMappingSchema`, so a field this app invented would be a field
 * the server refuses (rules §14.1).
 */
export type CrmMappingView = CrmMappingResponse['data'];
export type CrmPatientView = CrmPatientStatusResponse['data'];
export type CrmSyncNowResult = CrmSyncNowResponse['data'];
export type CrmResyncResult = CrmResyncResponse['data'];

export interface IntegrationsClient {
  /** `GET /admin/integrations` (integrations.read). `install` is null when not enabled. */
  list(): Promise<AdminIntegration[]>;
  /** `GET /admin/integrations/:key` (integrations.read). */
  get(key: string): Promise<AdminIntegration>;

  /**
   * `PUT /admin/integrations/:key/credentials`
   * (integrations.connect | integrations.rotate_credentials, **step-up**,
   * **no-impersonation**). The API stores the bundle encrypted and runs `testConnection()`
   * in the same request, which is why the answer is a probe and not just the install.
   */
  putCredentials(key: string, body: PutCredentialsRequest): Promise<ProbeResult>;
  /** `DELETE /admin/integrations/:key/credentials` (integrations.disable, **step-up**). */
  deleteCredentials(key: string): Promise<AdminIntegration>;

  /** `PATCH /admin/integrations/:key/settings` (integrations.connect). Non-secret only. */
  patchSettings(key: string, settings: Record<string, unknown>): Promise<AdminIntegration>;

  /** `POST /admin/integrations/:key/test` (integrations.test). 10 s budget. */
  test(key: string): Promise<ProbeResult>;
  /** `POST /admin/integrations/:key/enable` (integrations.connect). */
  enable(key: string): Promise<AdminIntegration>;
  /** `POST /admin/integrations/:key/disable` (integrations.disable, **step-up**). */
  disable(key: string, reason: string): Promise<AdminIntegration>;

  /** `GET /admin/integrations/:key/health` (integrations.read). */
  listHealth(key: string, query?: HealthQuery): Promise<IntegrationPage<IntegrationHealthCheck>>;
  /** `GET /admin/integrations/:key/calls` (integrations.read). */
  listCalls(key: string, query?: CallQuery): Promise<IntegrationPage<IntegrationCall>>;

  /* ------------------------------------------------------------------ CRM */

  /**
   * `GET /admin/integrations/highlevel/mapping` (integrations.read).
   *
   * Answers with `DEFAULT_CRM_MAPPING` for a clinic that has never opened the screen, so
   * the screen can be used to *set up* the sync and not only to review one. `installed`
   * and `enabled` come back with it, which is why the Marketing sync tab needs no second
   * call to know whether HighLevel is connected (`API.md` §2.10, FR-CRM-06).
   */
  getCrmMapping(): Promise<CrmMappingView>;

  /**
   * `PUT /admin/integrations/highlevel/mapping`
   * (integrations.connect, **no-impersonation**). Replaces the whole mapping.
   *
   * Tags go through the CRM scrubber on the way in: a tag naming a clinical term is
   * `400 VALIDATION_FAILED` with `details.issues[].path = ['tags', <event>, <index>]`,
   * which is what lets the screen put the message on the chip the admin typed.
   */
  putCrmMapping(mapping: CrmMapping): Promise<CrmMappingView>;

  /**
   * `POST /admin/integrations/highlevel/sync-now`
   * (integrations.connect, **no-impersonation**). Queues one page of the backfill.
   *
   * Answers what it queued, never what it did, and `queued: false` arrives with a
   * `reason` rather than an error: "you have not attested yet" is an answer, not a fault.
   *
   * `Partial`, because every field of `CrmSyncNowRequestSchema` has a default and an empty
   * body is the documented way to ask for the first page.
   */
  crmSyncNow(request?: Partial<CrmSyncNowRequest>): Promise<CrmSyncNowResult>;

  /** `GET /admin/patients/:id/crm` (patients.read). One patient's link and last runs. */
  getPatientCrm(patientId: string): Promise<CrmPatientView>;
  /** `POST /admin/patients/:id/crm/resync` (integrations.connect). Idempotent by hash. */
  resyncPatientCrm(patientId: string): Promise<CrmResyncResult>;

  /**
   * `POST /auth/two-factor/verify-totp` — the second factor a step-up route asked for.
   *
   * It sits on this client rather than on the app's auth client so a screen test can
   * drive the whole "refused, verify, retry" loop through one injected object.
   * **TODO(M2.7):** the shared step-up dialog owns this once `packages/ui/src/auth`
   * lands; this method then forwards to `apiClient.auth.verifyTotp`.
   */
  verifyTotp(code: string): Promise<void>;
}

/* ------------------------------------------------------------- real client */

const BASE = '/api/v1/admin/integrations';

/**
 * The CRM routes live on two bases: the mapping and the backfill are settings on the
 * HighLevel *install*, and the per-patient pair hangs off the patient record, because
 * that is where a staff member asks "is this person in our CRM" (`API.md` §2.10).
 */
const PATIENTS_BASE = '/api/v1/admin/patients';

/** The one CRM this milestone syncs to; `CrmMappingService.PROVIDER` on the API side. */
const CRM = `${BASE}/highlevel`;

/**
 * `put` is missing from `ApiClient`.
 *
 * `HttpMethod` in `@platform/api-client` is `get | post | patch | delete`, so the typed
 * helpers cannot express the one PUT this surface has. `request()` forwards the verb
 * verbatim (`method.toUpperCase()`), so the cast is a type-level gap and not a runtime
 * one. **TODO(M2.10):** add `put` to `HttpMethod` and a `put()` helper to `ApiClient`,
 * then delete this.
 */
const PUT = 'put' as HttpMethod;

/**
 * The typed `get`/`post`/`patch`/`delete` helpers are not used either: the OpenAPI
 * snapshot in `packages/api-client/openapi.json` predates the M3 controllers, so
 * `/api/v1/admin/integrations*` is absent from the generated `paths` and every call would
 * be a compile error on a path that exists. `request()` is the documented escape hatch;
 * the result types come from `@platform/contracts`, which is the contract of record.
 * **TODO(M3.x):** regenerate the document and switch to the typed helpers.
 */
export function createIntegrationsClient(api: ApiClient): IntegrationsClient {
  const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => {
    const body = await promise;
    /*
     * A 2xx that is not the documented envelope is a bug worth naming rather than a
     * `Cannot read properties of undefined` three components later (API.md §1.2).
     */
    if (body === null || typeof body !== 'object' || !('data' in body)) {
      throw new ApiError({
        status: 200,
        code: 'UNEXPECTED_RESPONSE',
        message: 'The server sent something we did not understand. Please try again.',
        correlationId: null,
      });
    }
    return body.data;
  };

  return {
    list: () => unwrap(api.request<{ data: AdminIntegration[] }>('get', BASE)),

    get: (key) => unwrap(api.request<{ data: AdminIntegration }>('get', `${BASE}/${path(key)}`)),

    putCredentials: (key, body) =>
      unwrap(api.request<{ data: ProbeResult }>(PUT, `${BASE}/${path(key)}/credentials`, body)),

    deleteCredentials: (key) =>
      unwrap(api.request<{ data: AdminIntegration }>('delete', `${BASE}/${path(key)}/credentials`)),

    patchSettings: (key, settings) =>
      unwrap(
        api.request<{ data: AdminIntegration }>('patch', `${BASE}/${path(key)}/settings`, {
          settings,
        }),
      ),

    test: (key) => unwrap(api.request<{ data: ProbeResult }>('post', `${BASE}/${path(key)}/test`)),

    enable: (key) =>
      unwrap(api.request<{ data: AdminIntegration }>('post', `${BASE}/${path(key)}/enable`)),

    disable: (key, reason) =>
      unwrap(
        api.request<{ data: AdminIntegration }>('post', `${BASE}/${path(key)}/disable`, { reason }),
      ),

    listHealth: (key, query = {}) =>
      api.request<IntegrationPage<IntegrationHealthCheck>>(
        'get',
        `${BASE}/${path(key)}/health`,
        undefined,
        { query: { limit: query.limit, cursor: query.cursor ?? undefined } },
      ),

    listCalls: (key, query = {}) =>
      api.request<IntegrationPage<IntegrationCall>>(
        'get',
        `${BASE}/${path(key)}/calls`,
        undefined,
        {
          query: {
            status: query.status,
            operation: query.operation,
            limit: query.limit,
            cursor: query.cursor ?? undefined,
          },
        },
      ),

    getCrmMapping: () => unwrap(api.request<{ data: CrmMappingView }>('get', `${CRM}/mapping`)),

    putCrmMapping: (mapping) =>
      unwrap(api.request<{ data: CrmMappingView }>(PUT, `${CRM}/mapping`, mapping)),

    crmSyncNow: (request = {}) =>
      unwrap(api.request<{ data: CrmSyncNowResult }>('post', `${CRM}/sync-now`, request)),

    getPatientCrm: (patientId) =>
      unwrap(
        api.request<{ data: CrmPatientView }>('get', `${PATIENTS_BASE}/${path(patientId)}/crm`),
      ),

    resyncPatientCrm: (patientId) =>
      unwrap(
        api.request<{ data: CrmResyncResult }>(
          'post',
          `${PATIENTS_BASE}/${path(patientId)}/crm/resync`,
        ),
      ),

    verifyTotp: (code) => api.auth.verifyTotp({ code }),
  };
}

/**
 * A catalog key is `^[a-z][a-z0-9-]*$` (`IntegrationKeySchema`), so it is already safe in
 * a path segment. Encoding it anyway costs nothing and means a key that one day widens -
 * or a caller that passes something else - cannot smuggle a `/` into the URL.
 */
function path(key: string): string {
  return encodeURIComponent(key);
}
